import { domToDataUrl } from 'modern-screenshot';
import {
  EMBEDDED_CONTENT_PLACEHOLDER_TEXT,
  EMBEDDED_CONTENT_SELECTOR,
  embeddedPlaceholderStyle,
} from './screenshot_placeholder';

export interface CapturedScreenshot {
  /** Raw base64 (no data: prefix) — what the backend DTO expects. */
  base64: string;
  contentType: 'image/jpeg';
  /** Full data URL for previewing in an <img>. */
  dataUrl: string;
}

/**
 * Resolve the effective page background, so the JPEG (no alpha channel)
 * doesn't render transparent areas black in dark mode.
 */
function resolveBackgroundColor(): string {
  for (const el of [document.body, document.documentElement]) {
    const bg = getComputedStyle(el).backgroundColor;
    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return bg;
  }
  return '#ffffff';
}

/**
 * antd popup layers that must not appear in the screenshot — most importantly
 * the feedback modal itself (it is already open while the capture runs) and
 * its mask, plus tooltips/toasts that only exist because of the interaction.
 */
const EXCLUDED_POPUP_SELECTOR =
  '.ant-modal-root, .ant-tooltip, .ant-message, .ant-notification, .ant-popover';

/**
 * Replaces every iframe / embed / object in the screenshot CLONE (never the
 * live page) with a labelled grey box of the same size. modern-screenshot
 * cannot render their content (cross-origin PDF preview, sandboxed mail
 * body), which otherwise shows up as a black area. Dropping the element also
 * keeps the iframe `src` — with the offer preview's Bearer token — out of the
 * rendered SVG.
 */
function replaceEmbeddedContent(cloneRoot: Node): void {
  if (!(cloneRoot instanceof Element)) return;
  const doc = cloneRoot.ownerDocument;
  for (const el of Array.from(cloneRoot.querySelectorAll(EMBEDDED_CONTENT_SELECTOR))) {
    const placeholder = doc.createElement('div');
    // modern-screenshot already inlined the computed style of the original
    // element (size, position, margins …) on the clone — keep that box.
    const sourceStyle = (el as HTMLElement).style;
    if (sourceStyle) placeholder.style.cssText = sourceStyle.cssText;
    for (const [prop, value] of Object.entries(
      embeddedPlaceholderStyle(sourceStyle?.display ?? ''),
    )) {
      placeholder.style.setProperty(prop, value);
    }
    const label = doc.createElement('span');
    label.textContent = EMBEDDED_CONTENT_PLACEHOLDER_TEXT;
    label.style.setProperty('padding', '0 16px');
    label.style.setProperty('max-width', '100%');
    placeholder.appendChild(label);
    el.replaceWith(placeholder);
  }
}

/**
 * Screenshot of the current page for the feedback dialog. Rendered client-side
 * from the DOM via modern-screenshot (handles the modern CSS — oklch etc. —
 * that html2canvas chokes on). Best-effort: returns null if capturing fails,
 * feedback can still be submitted without a screenshot.
 *
 * Safe to call while the feedback modal is open: popup layers are filtered
 * out, so the shot shows the page as it looked before the dialog appeared.
 * Embedded previews (iframes) appear as a labelled placeholder.
 */
export async function capturePageScreenshot(): Promise<CapturedScreenshot | null> {
  try {
    const dataUrl = await domToDataUrl(document.body, {
      type: 'image/jpeg',
      quality: 0.85,
      scale: 1,
      backgroundColor: resolveBackgroundColor(),
      filter: (node) =>
        !(node instanceof Element && node.matches(EXCLUDED_POPUP_SELECTOR)),
      onCloneNode: replaceEmbeddedContent,
    });
    const base64 = dataUrl.split(',')[1];
    if (!base64) return null;
    return { base64, contentType: 'image/jpeg', dataUrl };
  } catch (err) {
    console.warn('Screenshot für Feedback konnte nicht erstellt werden:', err);
    return null;
  }
}
